import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import { formatCustomerId, formatServerId } from '../utils/formatId';
import { useResources } from '../hooks/useResources';
import { Server, Plus, Play, Square, RotateCcw, Cpu, HardDrive, Users, Terminal, Settings, Copy, Check, Zap, Clock, Loader, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';

// Copy-to-clipboard field
const CopyField = ({ value, mono }) => {
  const [copied, setCopied] = React.useState(false);
  const copy = () => {
    navigator.clipboard.writeText(value || '');
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <button
      onClick={copy}
      className="flex items-center gap-2 bg-gray-800 hover:bg-gray-700 border border-gray-700 rounded-lg px-3 py-1.5 transition-colors group w-full"
    >
      <span className={`text-gray-200 text-xs truncate flex-1 text-left ${mono ? 'font-mono' : 'font-semibold'}`}>
        {value || '—'}
      </span>
      {copied
        ? <Check className="w-3.5 h-3.5 text-green-400 flex-shrink-0" />
        : <Copy className="w-3.5 h-3.5 text-gray-500 group-hover:text-gray-300 flex-shrink-0 transition-colors" />
      }
    </button>
  );
};

// Usage bar component
const UsageBar = ({ value, max, color = 'bg-green-500' }) => {
  const pct = max > 0 ? Math.min(100, (value / max) * 100) : 0;
  const barColor = pct > 85 ? 'bg-red-500' : pct > 65 ? 'bg-yellow-500' : color;
  return (
    <div className="w-full bg-gray-700/50 rounded-full h-1.5 mt-1.5">
      <div
        className={`h-1.5 rounded-full transition-all duration-500 ${barColor}`}
        style={{ width: `${pct}%` }}
      />
    </div>
  );
};

const STATUS_CONFIG = {
  running:    { color: 'text-green-400',  bg: 'bg-green-500/10  border-green-500/20',  dot: 'bg-green-400 animate-pulse',  label: 'Online' },
  stopped:    { color: 'text-gray-400',   bg: 'bg-gray-500/10   border-gray-500/20',   dot: 'bg-gray-400',                 label: 'Offline' },
  starting:   { color: 'text-blue-400',   bg: 'bg-blue-500/10   border-blue-500/20',   dot: 'bg-blue-400 animate-pulse',   label: 'Starting' },
  installing: { color: 'text-yellow-400', bg: 'bg-yellow-500/10 border-yellow-500/20', dot: 'bg-yellow-400 animate-pulse', label: 'Installing' },
  suspended:  { color: 'text-red-400',    bg: 'bg-red-500/10    border-red-500/20',    dot: 'bg-red-400',                  label: 'Suspended' },
};

// First-run helper: shows the newest server's address + join steps, dismissible
const GettingStarted = ({ servers }) => {
  const [dismissed, setDismissed] = React.useState(true);
  const newest = servers[0];

  React.useEffect(() => {
    if (newest) setDismissed(localStorage.getItem(`plarix_gs_${newest.id}`) === '1');
  }, [newest]);

  if (!newest || dismissed || !newest.ip_address) return null;
  const address = `${newest.ip_address}:${newest.port}`;

  return (
    <div className="md:col-span-2 xl:col-span-3 bg-gradient-to-r from-green-500/10 to-gray-900 border border-green-500/20 rounded-2xl p-5 mb-1">
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1">
          <div className="text-green-400 font-bold text-sm mb-2">🎮 Your server is ready — here's how to join</div>
          <ol className="text-gray-400 text-sm space-y-1 list-decimal list-inside">
            <li>Open Minecraft (Java Edition) → <span className="text-gray-300">Multiplayer</span> → <span className="text-gray-300">Add Server</span></li>
            <li>
              Paste this address:{' '}
              <button
                onClick={() => { navigator.clipboard.writeText(address); toast.success('Address copied!'); }}
                className="font-mono text-green-400 hover:text-green-300 bg-gray-800 px-2 py-0.5 rounded transition-colors"
              >
                {address} 📋
              </button>
            </li>
            <li>Join! Manage everything else (files, console, settings) from this dashboard.</li>
          </ol>
        </div>
        <button
          onClick={() => { localStorage.setItem(`plarix_gs_${newest.id}`, '1'); setDismissed(true); }}
          className="text-gray-600 hover:text-gray-400 transition-colors flex-shrink-0"
          aria-label="Dismiss"
        >✕</button>
      </div>
    </div>
  );
};

const ServerCard = ({ server, onStatusChange }) => {
  const { resources, loading: resLoading, liveStatus, formatBytes, formatUptime } = useResources(server.id, server.status);
  const [powering, setPowering] = React.useState(false);

  const displayStatus = liveStatus || server.status;
  const status = STATUS_CONFIG[displayStatus] || STATUS_CONFIG.stopped;
  const isRunning = displayStatus === 'running';
  const isStarting = displayStatus === 'starting' || displayStatus === 'installing';

  const handlePower = async (action) => {
    setPowering(true);
    try {
      await api.post(`/servers/${server.id}/power`, { action });
      toast.success(`Server ${action} successful`);
      if (onStatusChange) onStatusChange(server.id, action === 'stop' || action === 'kill' ? 'stopped' : 'running');
    } catch (err) {
      toast.error(err.response?.data?.error || `Failed to ${action} server`);
    } finally {
      setPowering(false);
    }
  };

  const handlePayOverdue = async () => {
    if (!window.confirm(`Pay the overdue renewal from your balance and unsuspend "${server.name}"?`)) return;
    setPowering(true);
    try {
      const { data } = await api.post(`/servers/${server.id}/pay-overdue`);
      toast.success(data.message);
      if (onStatusChange) onStatusChange(server.id, 'installing');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Payment failed');
    } finally {
      setPowering(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Are you sure you want to delete "${server.name}"? This cannot be undone and all data will be lost.`)) return;
    try {
      await api.delete(`/servers/${server.id}`);
      toast.success('Server deleted');
      if (onStatusChange) onStatusChange(server.id, 'deleted');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to delete server');
    }
  };

  const ramUsed   = resources?.resources?.memory_bytes || 0;
  const ramTotal  = resources?.resources?.memory_limit_bytes || server.ram_mb * 1024 * 1024;
  const cpuPct    = parseFloat(resources?.resources?.cpu_absolute || 0);
  const playersOn = resources?.players?.online || 0;
  const uptime    = resources?.resources?.uptime || 0;

  return (
    <div className="bg-gray-900 border border-gray-800 rounded-2xl p-6 hover:border-gray-700 transition-colors">
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-green-500/10 rounded-xl flex items-center justify-center">
            <Server className="w-5 h-5 text-green-400" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h3 className="font-bold text-white">{server.name}</h3>
              {server.server_number && (
                <span className="font-mono text-xs bg-gray-800 text-gray-500 px-2 py-0.5 rounded-lg">{formatServerId(server.server_number)}</span>
              )}
            </div>
            <p className="text-gray-500 text-sm">{server.plan_name} · {server.server_type || 'Paper'} {server.minecraft_version}</p>
          </div>
        </div>
        <div className={`flex items-center gap-1.5 border rounded-full px-3 py-1 text-xs font-medium ${status.bg} ${status.color}`}>
          <span className={`w-1.5 h-1.5 rounded-full ${status.dot}`} />
          {status.label}
        </div>
      </div>

      {/* Resource meters — only when running */}
      {isRunning && resources ? (
        <div className="grid grid-cols-3 gap-2.5 mb-4">
          {/* CPU */}
          <div className="bg-gray-800/50 rounded-xl p-3 min-w-0">
            <div className="flex items-center gap-1 text-gray-500 text-xs mb-1.5">
              <Zap className="w-3 h-3 flex-shrink-0" />
              <span className="truncate">CPU</span>
            </div>
            <div className={`text-base font-bold leading-none truncate ${cpuPct > 85 ? 'text-red-400' : cpuPct > 65 ? 'text-yellow-400' : 'text-white'}`}>
              {cpuPct.toFixed(0)}%
            </div>
            <UsageBar value={cpuPct} max={100} color="bg-blue-500" />
          </div>

          {/* RAM */}
          <div className="bg-gray-800/50 rounded-xl p-3 min-w-0">
            <div className="flex items-center gap-1 text-gray-500 text-xs mb-1.5">
              <Cpu className="w-3 h-3 flex-shrink-0" />
              <span className="truncate">RAM</span>
            </div>
            <div className={`text-base font-bold leading-none truncate ${(ramUsed / ramTotal) > 0.85 ? 'text-red-400' : (ramUsed / ramTotal) > 0.65 ? 'text-yellow-400' : 'text-white'}`}>
              {formatBytes(ramUsed)}
            </div>
            <UsageBar value={ramUsed} max={ramTotal} color="bg-green-500" />
            <div className="text-gray-600 text-[10px] mt-1 truncate">of {formatBytes(ramTotal)}</div>
          </div>

          {/* Players */}
          <div className="bg-gray-800/50 rounded-xl p-3 min-w-0">
            <div className="flex items-center gap-1 text-gray-500 text-xs mb-1.5">
              <Users className="w-3 h-3 flex-shrink-0" />
              <span className="truncate">Players</span>
            </div>
            <div className="text-base font-bold leading-none text-white truncate">
              {playersOn}<span className="text-gray-500 font-normal">/{server.player_slots}</span>
            </div>
            <UsageBar value={playersOn} max={server.player_slots} color="bg-purple-500" />
            {uptime > 0 ? (
              <div className="text-gray-600 text-[10px] mt-1 flex items-center gap-1 truncate">
                <Clock className="w-2.5 h-2.5 flex-shrink-0" /> {formatUptime(uptime)}
              </div>
            ) : (
              <div className="text-gray-700 text-[10px] mt-1">&nbsp;</div>
            )}
          </div>
        </div>
      ) : isRunning && resLoading ? (
        /* Loading skeleton */
        <div className="grid grid-cols-3 gap-2.5 mb-4">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="bg-gray-800/50 rounded-xl p-3 animate-pulse">
              <div className="h-3 bg-gray-700 rounded mb-2 w-1/2" />
              <div className="h-1.5 bg-gray-700 rounded" />
            </div>
          ))}
        </div>
      ) : (
        /* Static specs when offline */
        <div className="grid grid-cols-3 gap-2.5 mb-4">
          <div className="bg-gray-800/50 rounded-xl p-3 text-center">
            <Cpu className="w-4 h-4 text-gray-600 mx-auto mb-1" />
            <div className="text-gray-400 text-sm font-semibold">{server.ram_mb / 1024}GB</div>
            <div className="text-gray-600 text-xs">RAM</div>
          </div>
          <div className="bg-gray-800/50 rounded-xl p-3 text-center">
            <HardDrive className="w-4 h-4 text-gray-600 mx-auto mb-1" />
            <div className="text-gray-400 text-sm font-semibold">{server.disk_mb / 1024}GB</div>
            <div className="text-gray-600 text-xs">Storage</div>
          </div>
          <div className="bg-gray-800/50 rounded-xl p-3 text-center">
            <Users className="w-4 h-4 text-gray-600 mx-auto mb-1" />
            <div className="text-gray-400 text-sm font-semibold">{server.player_slots}</div>
            <div className="text-gray-600 text-xs">Slots</div>
          </div>
        </div>
      )}

      {/* Suspended — pay & unsuspend */}
      {displayStatus === 'suspended' && (
        <div className="bg-red-500/5 border border-red-500/20 rounded-xl p-3 mb-4 flex items-center justify-between gap-3">
          <div className="text-sm">
            <div className="text-red-400 font-semibold">Payment overdue</div>
            <div className="text-gray-500 text-xs mt-0.5">Pay now to bring your server back instantly.</div>
          </div>
          <button
            onClick={handlePayOverdue}
            disabled={powering}
            className="bg-red-500 hover:bg-red-400 disabled:opacity-50 text-white font-bold px-4 py-2 rounded-xl text-sm transition-colors flex-shrink-0"
          >
            Pay €{parseFloat(server.renewal_amount || server.price_monthly || 0).toFixed(2)} & Unsuspend
          </button>
        </div>
      )}

      {/* IP address */}
      {server.ip_address && (
        <button
          onClick={() => {
            navigator.clipboard.writeText(`${server.ip_address}:${server.port}`);
            toast.success('Server address copied');
          }}
          className="w-full flex items-center justify-between bg-gray-800/50 hover:bg-gray-800 rounded-xl px-3 py-2 text-sm text-gray-300 font-mono mb-4 transition-colors group"
        >
          <span className="truncate">{server.ip_address}:{server.port}</span>
          <Copy className="w-3.5 h-3.5 text-gray-600 group-hover:text-gray-400 flex-shrink-0 ml-2" />
        </button>
      )}

      {/* Actions */}
      <div className="flex gap-2">
        <button
          onClick={() => handlePower('start')}
          disabled={powering || isRunning}
          className="flex-1 flex items-center justify-center gap-1.5 bg-gray-800 hover:bg-green-500/10 hover:border-green-500/30 border border-gray-700 text-gray-300 hover:text-green-400 py-2 rounded-xl text-sm transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <Play className="w-4 h-4" /> Start
        </button>
        <button
          onClick={() => handlePower('stop')}
          disabled={powering || !isRunning}
          className="flex-1 flex items-center justify-center gap-1.5 bg-gray-800 hover:bg-red-500/10 hover:border-red-500/30 border border-gray-700 text-gray-300 hover:text-red-400 py-2 rounded-xl text-sm transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <Square className="w-4 h-4" /> Stop
        </button>
        <button
          onClick={() => handlePower('restart')}
          disabled={powering || !isRunning}
          className="flex-1 flex items-center justify-center gap-1.5 bg-gray-800 hover:bg-yellow-500/10 hover:border-yellow-500/30 border border-gray-700 text-gray-300 hover:text-yellow-400 py-2 rounded-xl text-sm transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {powering ? <Loader className="w-4 h-4 animate-spin" /> : <RotateCcw className="w-4 h-4" />} Restart
        </button>
        <Link to={`/manage/${server.id}`} className="flex items-center justify-center bg-gray-800 hover:bg-gray-700 border border-gray-700 text-gray-300 px-3 py-2 rounded-xl text-sm transition-colors" title="Manage">
          <Settings className="w-4 h-4" />
        </Link>
        <Link to={`/console/${server.id}`} className="flex items-center justify-center bg-gray-800 hover:bg-green-500/10 hover:border-green-500/30 border border-gray-700 text-gray-300 hover:text-green-400 px-3 py-2 rounded-xl text-sm transition-colors" title="Console">
          <Terminal className="w-4 h-4" />
        </Link>
        <button onClick={handleDelete} className="flex items-center justify-center bg-gray-800 hover:bg-red-500/10 hover:border-red-500/30 border border-gray-700 text-gray-300 hover:text-red-400 px-3 py-2 rounded-xl text-sm transition-colors" title="Delete server">
          <Trash2 className="w-4 h-4" />
        </button>
      </div>

      {/* Demo notice */}
      {isRunning && resources?.demo && (
        <p className="text-gray-700 text-xs mt-3 text-center">Demo data — connects to real metrics when Pterodactyl is configured</p>
      )}
    </div>
  );
};

const DashboardPage = () => {
  const { user } = useAuth();
  const [servers, setServers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [resendingVerification, setResendingVerification] = useState(false);
  const [verificationSent, setVerificationSent] = useState(false);

  useEffect(() => {
    api.get('/servers').then(({ data }) => {
      setServers(data.servers);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const resendVerification = async () => {
    setResendingVerification(true);
    try {
      await api.post('/auth/send-verification');
      setVerificationSent(true);
    } catch {}
    finally { setResendingVerification(false); }
  };

  return (
    <div className="min-h-screen bg-gray-950 pt-20 px-4 pb-12">
      <div className="max-w-6xl mx-auto">

        {/* Email verification banner */}
        {user && !user.email_verified && (
          <div className="flex items-center gap-3 bg-yellow-500/10 border border-yellow-500/20 rounded-2xl px-5 py-4 mb-6">
            <span className="text-xl">📧</span>
            <div className="flex-1">
              <div className="font-semibold text-yellow-400 text-sm">Verify your email address</div>
              <div className="text-yellow-400/70 text-xs mt-0.5">Check your inbox for a verification link to unlock all features.</div>
            </div>
            {verificationSent ? (
              <span className="text-green-400 text-xs font-medium">✓ Email sent!</span>
            ) : (
              <button
                onClick={resendVerification}
                disabled={resendingVerification}
                className="text-xs bg-yellow-500/20 hover:bg-yellow-500/30 border border-yellow-500/30 text-yellow-400 px-3 py-1.5 rounded-lg transition-colors flex-shrink-0 disabled:opacity-50"
              >
                {resendingVerification ? 'Sending...' : 'Resend email'}
              </button>
            )}
          </div>
        )}

        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-black text-white">
              Hey, {user?.first_name} 👋
            </h1>
            <p className="text-gray-400 mt-1 flex items-center gap-2">
              {servers.length === 0
                ? 'No servers yet — deploy your first one below'
                : `You have ${servers.length} server${servers.length !== 1 ? 's' : ''}`}
              {user?.customer_number && (
                <span className="font-mono text-xs bg-gray-800 text-gray-500 px-2 py-0.5 rounded-lg">
                  {formatCustomerId(user.customer_number)}
                </span>
              )}
            </p>
          </div>
          <Link
            to="/pricing"
            className="flex items-center gap-2 bg-green-500 hover:bg-green-400 text-gray-950 font-bold px-4 py-2.5 rounded-xl text-sm transition-colors"
          >
            <Plus className="w-4 h-4" /> New Server
          </Link>
        </div>

        {/* Servers grid */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="bg-gray-900 border border-gray-800 rounded-2xl p-6 animate-pulse">
                <div className="flex gap-3 mb-4">
                  <div className="w-10 h-10 bg-gray-800 rounded-xl" />
                  <div className="flex-1">
                    <div className="h-4 bg-gray-800 rounded w-3/4 mb-2" />
                    <div className="h-3 bg-gray-800 rounded w-1/2" />
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-3 mb-4">
                  {[...Array(3)].map((_, j) => <div key={j} className="h-16 bg-gray-800 rounded-xl" />)}
                </div>
              </div>
            ))}
          </div>
        ) : servers.length === 0 ? (
          <div className="text-center py-24 bg-gray-900/50 border border-gray-800 border-dashed rounded-2xl">
            <div className="w-16 h-16 bg-green-500/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Server className="w-8 h-8 text-green-400" />
            </div>
            <h2 className="text-xl font-bold text-white mb-2">No servers yet</h2>
            <p className="text-gray-400 mb-6">Deploy your first Minecraft server in under 30 seconds.</p>
            <Link
              to="/pricing"
              className="inline-flex items-center gap-2 bg-green-500 hover:bg-green-400 text-gray-950 font-bold px-6 py-3 rounded-xl transition-colors"
            >
              <Plus className="w-4 h-4" /> Deploy your first server
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <GettingStarted servers={servers} />
            {servers.map((s) => (
              <ServerCard
                key={s.id}
                server={s}
                onStatusChange={(id, newStatus) => {
                  if (newStatus === 'deleted') {
                    setServers(prev => prev.filter(srv => srv.id !== id));
                  } else {
                    setServers(prev => prev.map(srv => srv.id === id ? { ...srv, status: newStatus } : srv));
                  }
                }}
              />
            ))}
          </div>
        )}

        {/* Account info */}
        <div className="mt-12 bg-gray-900 border border-gray-800 rounded-2xl p-5">
          <h2 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-4">Account Info</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
            <div>
              <div className="text-gray-500 text-xs mb-1">Customer ID</div>
              <CopyField value={formatCustomerId(user?.customer_number)} />
            </div>
            <div>
              <div className="text-gray-500 text-xs mb-1">Full UUID</div>
              <CopyField value={user?.id} mono />
            </div>
            <div>
              <div className="text-gray-500 text-xs mb-1">Email</div>
              <div className="text-gray-300 font-mono text-xs truncate">{user?.email}</div>
            </div>
          </div>
          <p className="text-gray-600 text-xs mt-3">Share your Customer ID with support when reporting an issue.</p>
        </div>

        {/* Quick links */}
        <div className="mt-12 grid grid-cols-1 md:grid-cols-4 gap-4">
          {[
            { icon: '💳', title: 'Billing', desc: 'Invoices and payment history', href: '/billing' },
            { icon: '💰', title: 'Balance', desc: 'Credits and auto-renewals', href: '/balance' },
            { icon: '🎫', title: 'Support', desc: 'Open or view support tickets', href: '/tickets' },
            { icon: '📖', title: 'Docs', desc: 'Guides and tutorials', href: '/docs' },
          ].map(({ icon, title, desc, href }) => (
            <Link key={title} to={href} className="bg-gray-900 border border-gray-800 hover:border-gray-700 rounded-2xl p-5 flex items-center gap-4 transition-colors group">
              <div className="text-2xl">{icon}</div>
              <div>
                <div className="font-semibold text-white group-hover:text-green-400 transition-colors">{title}</div>
                <div className="text-gray-500 text-sm">{desc}</div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;
